/**
 * SC-209: which kind of picture-in-picture this browser can give us, and whether we are allowed to offer it.
 *
 * **Feature detection only. No user-agent sniffing anywhere under `pip/`.** The three engines behave
 * differently enough that it is tempting, and every version of that check rots: Firefox gained Document
 * picture-in-picture in 151, and a string test written before that would still be refusing it.
 *
 * The two capability inputs are passed in rather than read here so this stays pure and testable. The caller
 * does the detection, which is two expressions and has no interesting failure mode.
 */

import type { WorkspaceFeatureFlags } from '../../../core/models/Workspace'

import type { PipMode } from './pipModel'

export interface PipCapabilityInput {
  /** The application-wide switch (`WorkspaceSettings.features.pictureInPicture`) — see `isPipFeatureEnabled`. */
  flagOn: boolean
  /** `'documentPictureInPicture' in window` — Chromium since 2023, Firefox since 151. */
  hasDocumentPip: boolean
  /** `document.pictureInPictureEnabled === true` — the older one-video API, which is all Safari has. */
  hasVideoPip: boolean
}

/**
 * Document picture-in-picture wins wherever it exists, because only a real document can carry the tile grid,
 * the participant count and working buttons. The single-video mode is Safari's reduced path (phase 4): it
 * shows one thing and has no chrome of our own.
 */
export function pipCapability(input: PipCapabilityInput): PipMode {
  if (!input.flagOn) return 'none'
  if (input.hasDocumentPip) return 'document'
  if (input.hasVideoPip) return 'video'
  return 'none'
}

/**
 * Reads the one application-wide switch, from the global `WorkspaceSettings` the app already loads on mount.
 *
 * Strictly `=== true`: absent means off, so an office nobody has turned this on for stays off, and a value
 * that arrives as a string from Parse's untyped `features` object does not read as enabled.
 */
export function isPipFeatureEnabled(settings: { features?: WorkspaceFeatureFlags } | null | undefined): boolean {
  return settings?.features?.pictureInPicture === true
}
